import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomButton from "../Common/CustomButton/CustomButton";
import { Card } from "primereact/card";
import "./BriefingDocument.scss";
import { fetchWithHeaders } from "../../api/fetchWithHeaders";
import { useToastStore } from "../../stores/toastStore";
import CustomDropdown from "../Common/CustomDropdown/CustomDropdown";
import { useNavigationContextStore } from "../../stores/navigationContextStore";

type SupplierOption = {
	supplier_id: number;
	supplier_name: string;
	supplier_code: string | null;
};
type NegotiationAskResponse = {
	content?: string;
	role?: string;
	timestamp?: number;
};

type TargetHeading = {
	product_description: string;
	bid_id: number;
};

type SummaryEntry = {
	product_name: string;
	buyer_targets?: { target_price: number | null; supplier_bid: number | null };
	category_goals?:
	| string
	| Record<string, unknown>
	| {
		parameter_name?: string;
		objective?: string;
		rationale?: string;
		leverage_level?: string;
	}[];
	mdo?: { target_price: number | null; supplier_bid: number | null };
	price_laa?: { target_price: number | null; supplier_bid: number | null };
	batna?: string | null;
};

type TacticEntry = {
	title?: string;
	description?: string;
	bullets?: string[];
};
type NormalizedTacticEntry = {
	title: string;
	description: string;
	bullets: string[];
};

type NormalizedRiskWatchout = {
	title: string;
	risk: string;
	watchout: string;
};

type RiskItem = {
	title?: string;
	description?: string;
	risk?: string;
	watchout?: string;
};

type TacticsData = {
	strategy?: Array<string | TacticEntry>;
	counter_strategy?: Array<string | TacticEntry>;
	concessions?: Array<string | TacticEntry>;
	risks_watchouts?: Array<{
		title?: string;
		risk?: string | RiskItem;
		watchout?: string | RiskItem;
	}> | {
		title?: string;
		risk?: string | RiskItem;
		watchout?: string | RiskItem;
	};
};

type TargetCard = {
	label: string;
	subLabel: string;
	value: string | null;
	supplierBid?: string | null;
	bullets?: string[];
	isBatna?: boolean;
};

type CurrentOfferRow = {
	article: string | null;
	variant: string | null;
	pack_size: number | null;
	quality: string | null;
	country: string | null;
	incoterm: string | null;
	unit_cost_exw: number | null;
	unit_cost_logistics: number | null;
	unit_cost_ddp: number | null;
	proposed_volume_allocation: number | null;
	proposed_volume_allocation_change_pct?: number | null;
	total_cpv_potential: number | null;
	total_cpv_potential_change_pct?: number | null;
};

const BriefingDocument = () => {
	const { error } = useToastStore.getState();
	const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
	const [targets, setTargets] = useState<TargetHeading[]>([]);
	const [loadingSuppliers, setLoadingSuppliers] = useState(false);
	const [loadingTargets, setLoadingTargets] = useState(false);
	const [loadingKeyAsk, setLoadingKeyAsk] = useState(false);
	const [loadingSummary, setLoadingSummary] = useState(false);
	const [loadingTactics, setLoadingTactics] = useState(false);
	const [keyAskItems, setKeyAskItems] = useState<string[] | null>(null);
	const [summaryTargets, setSummaryTargets] = useState<SummaryEntry[]>([]);
	const [tactics, setTactics] = useState<TacticsData | null>(null);
	const [currentOfferRows, setCurrentOfferRows] = useState<CurrentOfferRow[]>([]);
	const [loadingCurrentOffer, setLoadingCurrentOffer] = useState(false);
	const [categoryGoalsLoading, setCategoryGoalsLoading] = useState<Record<string, boolean>>({});
	const [categoryGoalsFetched, setCategoryGoalsFetched] = useState<Record<string, boolean>>({});
	const [exporting, setExporting] = useState(false);
	const exportRef = useRef<HTMLDivElement | null>(null);
	const exportHiddenRef = useRef<HTMLDivElement | null>(null);
	const {
		selectedSupplierId,
		setSelectedSupplierId,
	} = useNavigationContextStore();
	const negotiationId = useMemo(() => {
		const workspace = JSON.parse(
			sessionStorage.getItem("negotiation_workspace") || "{}"
		);
		return workspace?.negotiation_id ?? 2;
	}, []);

	useEffect(() => {
		const fetchSuppliers = async () => {
			setLoadingSuppliers(true);
			const base = import.meta.env.VITE_API_BASE_URL || "";
			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/tenders/negotiation-packages/${negotiationId}/suppliers`,
					{
						method: "GET",
					}
				);
				const data: SupplierOption[] = await resp.json();
				setSuppliers(data);
				if (data.length > 0) {
					setSelectedSupplierId(data[0].supplier_id);
				}
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Unable to load suppliers";
				error("Error", message);
			} finally {
				setLoadingSuppliers(false);
			}
		};

		fetchSuppliers();
	}, [negotiationId, error]);


	useEffect(() => {
		const controller = new AbortController();
		const fetchTargets = async () => {
			if (!selectedSupplierId) return;
			setLoadingTargets(true);
			const base = import.meta.env.VITE_API_BASE_URL || "";
			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/tenders/negotiation-packages/${negotiationId}/suppliers/${selectedSupplierId}`,
					{ method: "GET", signal: controller.signal }
				);
				const data: TargetHeading[] = await resp.json();
				setTargets(data);
			} catch (err: any) {
				if (err?.name === "AbortError") return;
				const message =
					err instanceof Error ? err.message : "Unable to load targets";
				error("Error", message);
				setTargets([]);
			} finally {
				setLoadingTargets(false);
			}
		};

		fetchTargets();
		return () => controller.abort();
	}, [selectedSupplierId, negotiationId, error]);

	useEffect(() => {
		const controller = new AbortController();
		const fetchCurrentOffer = async () => {
			if (!selectedSupplierId) return;
			setLoadingCurrentOffer(true);
			const base = import.meta.env.VITE_API_BASE_URL || "";
			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/briefing-document/current-offer?negotiation_id=${negotiationId}&supplier_id=${selectedSupplierId}`,
					{ method: "GET", signal: controller.signal }
				);
				const data: CurrentOfferRow[] = await resp.json();
				setCurrentOfferRows(Array.isArray(data) ? data : []);
			} catch (err: any) {
				if (err?.name === "AbortError") return;
				const message =
					err instanceof Error ? err.message : "Unable to load current offer";
				error("Error", message);
				setCurrentOfferRows([]);
			} finally {
				setLoadingCurrentOffer(false);
			}
		};

		const fetchKeyAsk = async () => {
			if (!selectedSupplierId) return;
			setLoadingKeyAsk(true);
			const base = import.meta.env.VITE_API_BASE_URL || "";
			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/briefing-document/negotiation_ask?negotiation_id=${negotiationId}&supplier_id=${selectedSupplierId}`,
					{ method: "GET", signal: controller.signal }
				);
				const data: NegotiationAskResponse = await resp.json();
				if (data?.content) {
					const lines = String(data.content)
						.split("\n")
						.map((l: string) => l.trim())
						.filter((l: string) => l.startsWith("-"));
					const cleaned = lines.map((l: string) => l.replace(/^-+\s*/, ""));
					setKeyAskItems(cleaned);
				} else {
					setKeyAskItems(null);
				}
			} catch (err: any) {
				if (err?.name === "AbortError") return;
				const message =
					err instanceof Error ? err.message : "Unable to load key ask";
				error("Error", message);
				setKeyAskItems(null);
			} finally {
				setLoadingKeyAsk(false);
			}
		};

		const fetchSummary = async () => {
			if (!selectedSupplierId) return;
			setLoadingSummary(true);
			const base = import.meta.env.VITE_API_BASE_URL || "";
			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/briefing-document/summary?negotiation_id=${negotiationId}&supplier_id=${selectedSupplierId}`,
					{ method: "GET", signal: controller.signal }
				);
				const data: SummaryEntry[] | SummaryEntry = await resp.json();
				const normalizedSummary = Array.isArray(data)
					? data
					: data
						? [data]
						: [];
				setSummaryTargets(normalizedSummary as SummaryEntry[]);
			} catch (err: any) {
				if (err?.name === "AbortError") return;
				const message =
					err instanceof Error ? err.message : "Unable to load summary";
				error("Error", message);
				setSummaryTargets([]);
			} finally {
				setLoadingSummary(false);
			}
		};

		const fetchTactics = async () => {
			if (!selectedSupplierId) return;
			setLoadingTactics(true);
			const base = import.meta.env.VITE_API_BASE_URL || "";
			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/briefing-document/tactics?negotiation_id=${negotiationId}&supplier_id=${selectedSupplierId}`,
					{ method: "GET", signal: controller.signal }
				);
				const data: TacticsData = await resp.json();
				setTactics(data || null);
			} catch (err: any) {
				if (err?.name === "AbortError") return;
				const message =
					err instanceof Error ? err.message : "Unable to load tactics";
				error("Error", message);
				setTactics(null);
			} finally {
				setLoadingTactics(false);
			}
		};

		fetchCurrentOffer();
		fetchKeyAsk().then(() => {
			fetchSummary();
			fetchTactics();
		});
		return () => controller.abort();
	}, [selectedSupplierId, negotiationId, error]);

	const renderKeyAsk = (item: string) => {
		const [prefix, ...rest] = item.split(":");
		if (!rest.length) return item;
		const suffix = rest.join(":").trim();
		return (
			<>
				<strong>{prefix.trim()}:</strong> {suffix}
			</>
		);
	};
	const renderWithPrefixBold = (text: string | RiskItem | null | undefined) => {
		if (!text) return null;
		if (typeof text === "string") {
			const [, ...rest] = text.split(":");
			if (!rest.length) return text;
			return rest.join(":").trim();
		}

		const title = text.title?.trim();
		const body = (text.description ?? text.risk ?? text.watchout ?? "")
			.toString()
			.trim();

		if (title && body) return body;
		if (body) return body;
		return null;
	};

	const resolvedKeyAsk = useMemo(() => {
		if (keyAskItems && keyAskItems.length > 0) return keyAskItems;
		return [];
	}, [keyAskItems]);

	const normalizeProductKey = (name?: string | null) => (name || "").trim().toLowerCase();

	const mergeSummaryTargets = (newEntries: SummaryEntry[]) =>
		setSummaryTargets((prev) => {
			if (!newEntries.length) return prev;

			const existingMap = new Map<string, SummaryEntry>();
			prev.forEach((entry) => {
				const key = normalizeProductKey(entry.product_name);
				if (key) existingMap.set(key, entry);
			});

			newEntries.forEach((entry) => {
				const key = normalizeProductKey(entry.product_name);
				if (key) existingMap.set(key, entry);
			});

			const merged: SummaryEntry[] = [];
			const seen = new Set<string>();

			// Preserve existing order where possible
			prev.forEach((entry) => {
				const key = normalizeProductKey(entry.product_name);
				if (key && existingMap.has(key) && !seen.has(key)) {
					merged.push(existingMap.get(key)!);
					seen.add(key);
				}
			});

			// Append any brand new entries
			newEntries.forEach((entry) => {
				const key = normalizeProductKey(entry.product_name);
				if (key && !seen.has(key)) {
					merged.push(entry);
					seen.add(key);
				}
			});

			return merged;
		});

	const summaryByProduct = useMemo(() => {
		const map = new Map<string, SummaryEntry>();
		(summaryTargets || []).forEach((entry) => {
			const key = normalizeProductKey(entry.product_name);
			if (key) map.set(key, entry);
		});
		return map;
	}, [summaryTargets]);

	const normalizeRiskWatchouts = (risks?: TacticsData["risks_watchouts"]): NormalizedRiskWatchout[] => {
		if (!risks) return [];
		const list = Array.isArray(risks) ? risks : [risks];
		return list
			.map((item) => {
				const title = (item?.title || "").toString().trim();
				const risk = (renderWithPrefixBold(item?.risk) || "").toString().trim();
				const watchout = (renderWithPrefixBold(item?.watchout) || "").toString().trim();
				return { title, risk, watchout };
			})
			.filter((item) => item.title || item.risk || item.watchout);
	};

	const resolvedRisks = useMemo(
		() => normalizeRiskWatchouts(tactics?.risks_watchouts),
		[tactics?.risks_watchouts]
	);

	const normalizeTacticItem = (item: string | TacticEntry): NormalizedTacticEntry => {
		if (typeof item === "string") {
			return { description: item, title: "", bullets: [] };
		}

		return {
			title: item?.title || "",
			description: item?.description || "",
			bullets: (item?.bullets || []).filter(Boolean),
		};
	};

const formatCurrency = (val: number | null | undefined) => {
	if (val === null || val === undefined) return null;
	return `EUR ${val}`;
};

	const normalizeCategoryGoals = (
		categoryGoals: SummaryEntry["category_goals"]
	): string[] => {
		if (!categoryGoals) return [];

		// Already an array of structured goal items
		if (Array.isArray(categoryGoals)) {
			return categoryGoals
				.map((goal) => goal?.rationale || goal?.objective || goal?.parameter_name)
				.filter((text): text is string => Boolean(text));
		}

		// Plain string
		if (typeof categoryGoals === "string") {
			return [categoryGoals];
		}

		// Generic object: pick string-valued fields, ignore nested objects
		if (typeof categoryGoals === "object") {
			return Object.values(categoryGoals)
				.filter((val) => typeof val === "string")
				.map((val) => val as string);
		}

		return [];
	};

	const buildSummaryCards = (entry?: SummaryEntry | null): TargetCard[] => {
		if (!entry) return [];

		const cards: TargetCard[] = [];

		if (entry.buyer_targets) {
			cards.push({
				label: "Kearney Target",
				subLabel: "Target Price",
				value: formatCurrency(entry.buyer_targets.target_price),
				supplierBid: formatCurrency(entry.buyer_targets.supplier_bid),
			});
		}

		if (entry.category_goals) {
			const goals = normalizeCategoryGoals(entry.category_goals);

			if (goals.length) {
				cards.push({
					label: "Category Goals",
					subLabel: "Additional Considerations",
					value: "",
					bullets: goals as string[],
				});
			}
		}

		if (entry.mdo) {
			cards.push({
				label: "MDO",
				subLabel: "(Most Desired Outcome)",
				value: formatCurrency(entry.mdo.target_price),
				supplierBid: formatCurrency(entry.mdo.supplier_bid),
			});
		}

		if (entry.price_laa) {
			cards.push({
				label: "LAA",
				subLabel: "(Least Acceptable Agreement)",
				value: formatCurrency(entry.price_laa.target_price),
				supplierBid: formatCurrency(entry.price_laa.supplier_bid),
			});
		}

		if (entry.batna) {
			cards.push({
				label: "BATNA",
				subLabel: "(Best Alternative to a Negotiated Agreement)",
				value: entry.batna,
				isBatna: true,
			});
		}

		return cards;
	};

	const fallbackSummaryCards = useMemo(() => {
		const firstEntry = summaryTargets[0];
		return buildSummaryCards(firstEntry);
	}, [summaryTargets]);

	useEffect(() => {
		setCategoryGoalsFetched((prev) => {
			const next = { ...prev };
			(summaryTargets || []).forEach((entry) => {
				const productKey = normalizeProductKey(entry.product_name);
				if (!productKey) return;
				if (normalizeCategoryGoals(entry.category_goals).length > 0) {
					next[productKey] = true;
				}
			});
			return next;
		});
	}, [summaryTargets]);

	const handleFetchCategoryGoals = useCallback(
		async (productName: string) => {
			const productKey = normalizeProductKey(productName);
			if (!productKey || !selectedSupplierId) return;
			if (categoryGoalsLoading[productKey] || categoryGoalsFetched[productKey]) return;

			setCategoryGoalsLoading((prev) => ({ ...prev, [productKey]: true }));
			const base = import.meta.env.VITE_API_BASE_URL || "";

			try {
				const resp = await fetchWithHeaders(
					`${base}api/v1/briefing-document/category_goals?negotiation_id=${negotiationId}&supplier_id=${selectedSupplierId}&product_description=${encodeURIComponent(productName)}`,
					{ method: "GET" }
				);
				if (!resp.ok) {
					const errText = await resp.text();
					throw new Error(errText || "Unable to load category goals");
				}
				const summarydata: SummaryEntry[] | SummaryEntry = await resp.json();
				const normalizedSummary = Array.isArray(summarydata)
					? summarydata
					: summarydata
						? [summarydata]
						: [];
				mergeSummaryTargets(normalizedSummary as SummaryEntry[]);
				setCategoryGoalsFetched((prev) => ({ ...prev, [productKey]: true }));
			} catch (err) {
				const message = err instanceof Error ? err.message : "Unable to load category goals";
				error("Error", message);
			} finally {
				setCategoryGoalsLoading((prev) => ({ ...prev, [productKey]: false }));
			}
		},
		[
			categoryGoalsFetched,
			categoryGoalsLoading,
			error,
			negotiationId,
			mergeSummaryTargets,
			selectedSupplierId,
		]
	);

	const showSectionLoader =
		loadingKeyAsk ||
		loadingSummary ||
		loadingTactics ||
		loadingTargets ||
		loadingCurrentOffer;

	if (!loadingSuppliers && suppliers.length === 0) {
		return null;
	}

	const displaySupplierName =
		suppliers.find((s) => s.supplier_id === selectedSupplierId)
			?.supplier_name || "";
	const displayPointOfContact = "—";

	const supplierOptions = suppliers.map((s) => ({
		name: s.supplier_name,
		code: s.supplier_id.toString(),
	}));

	const selectedSupplierCode =
		selectedSupplierId !== null ? selectedSupplierId.toString() : null;
	const selectedSupplierOption =
		supplierOptions.find((opt) => opt.code === selectedSupplierCode) || null;

	const handleSupplierSelect = (opt: { code?: string | null }) => {
		setLoadingKeyAsk(true);
		setLoadingSummary(false);
		setLoadingTactics(true);
		setLoadingTargets(true);
		setLoadingCurrentOffer(true);
		setCategoryGoalsFetched({});
		setCategoryGoalsLoading({});
		setSummaryTargets([]);
		setKeyAskItems(null);
		setTactics(null);
		setSelectedSupplierId(opt?.code ? Number(opt.code) : null);
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			if (!selectedSupplierId) {
				error("Error", "Please select a supplier before exporting.");
				setExporting(false);
				return;
			}
			const base = import.meta.env.VITE_API_BASE_URL || "";
			const resp = await fetchWithHeaders(
				`${base}api/v1/briefing-document/export-briefing-pdf?negotiation_id=${negotiationId}&supplier_id=${selectedSupplierId}`,
				{ method: "GET" }
			);
			if (!resp.ok) {
				const errText = await resp.text();
				throw new Error(errText || "Failed to export PDF");
			}
			const blob = await resp.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `briefing_${(displaySupplierName || "supplier")
				.replace(/[^a-z0-9_-]/gi, "_")
				.toLowerCase()}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			error("Error", "Failed to export PDF");
			console.error(err);
		} finally {
			setExporting(false);
		}
	};

	if (!loadingSuppliers && suppliers.length === 0) {
		return (
			<div className="briefing-doc briefing-doc__generation">
				<div className="briefing-doc__generation-card">
					<div className="briefing-doc__section-heading">
						Generation in progress…
					</div>
					<p className="briefing-doc__muted">
						Your briefing document is being prepared. Please check back in a
						moment.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="briefing-doc" ref={exportRef}>
			{showSectionLoader && (
				<div className="briefing-doc__inline-loader briefing-doc__inline-loader--fixed">
					<ProgressSpinner
						style={{ width: "50px", height: "50px" }}
						strokeWidth="6"
						fill="transparent"
						animationDuration=".6s"
					/>
				</div>
			)}

			<div className="briefing-doc__selector">
				<Card className="briefing-doc__selector-card">
					<div className="briefing-doc__selector-field">
						<label className="briefing-doc__selector-label">Select Supplier:</label>
						<CustomDropdown
							selectedOption={selectedSupplierOption}
							options={supplierOptions}
							placeholder="Select supplier..."
							onSelect={handleSupplierSelect}
						/>
					</div>
				</Card>
			</div>

			<div className="briefing-doc__info-bar">
				<div className="briefing-doc__info-card">
					<span className="briefing-doc__info-label">Supplier Name:</span>
					<span className="briefing-doc__info-value">{displaySupplierName || "—"}</span>
				</div>
				<div className="briefing-doc__info-card">
					<span className="briefing-doc__info-label">Supplier Point of Contact:</span>
					<span className="briefing-doc__info-value">{displayPointOfContact}</span>
				</div>
			</div>

			<div className="briefing-doc__section briefing-doc__key-ask-header">
				<div className="briefing-doc__section-heading briefing-doc__key-ask-title">
					Key Ask
				</div>
			</div>

			<div className="briefing-doc__key-ask">
				<div className="briefing-doc__key-ask-body">
					<ul className="briefing-doc__list">
						{resolvedKeyAsk.map((item) => (
							<li key={item}>{renderKeyAsk(item)}</li>
						))}
						{loadingKeyAsk && (
							<li className="briefing-doc__muted">Loading key asks…</li>
						)}
						{!loadingKeyAsk && resolvedKeyAsk.length === 0 && (
							<li className="briefing-doc__empty-state">No key ask available</li>
						)}
					</ul>
				</div>
			</div>

			<div className="briefing-doc__targets">
				<div className="briefing-doc__text briefing-doc__targets-heading">
					<div className="briefing-doc__section-heading">Targets</div>
				</div>
				<div className="briefing-doc__target-accordions">
					{loadingTargets && <div className="briefing-doc__muted">Loading targets…</div>}
					{loadingSummary && <div className="briefing-doc__muted">Loading target summary…</div>}
					{!loadingTargets && targets.length === 0 && (
						<div className="briefing-doc__muted">
							No targets available for this supplier.
						</div>
					)}
					{targets.map((target) => {
						const productKey = normalizeProductKey(target.product_description);
						const summaryEntry = summaryByProduct.get(productKey);
						const summaryCardsForTarget = summaryEntry
							? buildSummaryCards(summaryEntry)
							: [];
						const isGoalsLoading = Boolean(productKey && categoryGoalsLoading[productKey]);
						const hasFetched = Boolean(productKey && categoryGoalsFetched[productKey]);
						return (
							<TargetAccordion
								key={`${target.product_description}-${target.bid_id}`}
								title={target.product_description}
								targetCards={summaryCardsForTarget}
								defaultOpen={false}
								onOpen={() => handleFetchCategoryGoals(target.product_description)}
								goalsLoading={isGoalsLoading}
								dataFetched={hasFetched || summaryCardsForTarget.length > 0}
							/>
						);
					})}
				</div>
			</div>

			<div className="briefing-doc__section briefing-doc__current-offer-header">
				<div className="briefing-doc__section-heading">Current Offer</div>
			</div>
			<div className="briefing-doc__current-offer">
				<div className="briefing-doc__table-wrapper">
					<table>
						<thead>
							<tr>
								<th>
									Article
								</th>
								<th>Variant</th>
								<th>Pack Size</th>
								<th>Quality</th>
								<th>Country</th>
								<th>Incoterm</th>
								<th>
									<div>Unit Cost (EXW)</div>
									<div className="briefing-doc__current-offer-sub">(last price paid)</div>
									<div className="briefing-doc__current-offer-sub">(EUR)</div>
								</th>
								{/* <th>
									<div>Unit Cost Logistics</div>
									<div className="briefing-doc__current-offer-sub">last price paid</div>
									<div className="briefing-doc__current-offer-sub">(EUR)</div>
								</th> */}
								<th>
									<div>Unit Cost DDP</div>
									<div className="briefing-doc__current-offer-sub">(last price paid)</div>
									<div className="briefing-doc__current-offer-sub">(EUR)</div>
								</th>
								<th>
									<div>Proposed</div>
									<div>Volume Allocation</div>
									<div className="briefing-doc__current-offer-sub">(% change)</div>
								</th>
								<th>
									<div>Total</div>
									<div>CPV Potential</div>
									<div className="briefing-doc__current-offer-sub">(% change) (EUR)</div>
								</th>
							</tr>
						</thead>
						<tbody>
							{loadingCurrentOffer && (
								<tr>
									<td colSpan={11} className="text-center text-sm text-gray-500">
										Loading current offer…
									</td>
								</tr>
							)}
							{!loadingCurrentOffer && currentOfferRows.length === 0 && (
								<tr>
									<td colSpan={11} className="text-center text-sm text-gray-500">
										No current offer data available.
									</td>
								</tr>
							)}
							{!loadingCurrentOffer &&
								currentOfferRows.map((row, idx) => (
									<tr key={`${row.article}-${idx}`}>
										<td>{row.article || "—"}</td>
										<td>{row.variant || "—"}</td>
										<td>{row.pack_size ?? "—"}</td>
										<td>{row.quality || "—"}</td>
										<td>{row.country || "—"}</td>
										<td>{row.incoterm || "—"}</td>
										<td>{row.unit_cost_exw ?? "—"}</td>
										{/* <td>{row.unit_cost_logistics ?? "—"}</td> */}
										<td>{row.unit_cost_ddp ?? "—"}</td>
										<td>{row.proposed_volume_allocation ?? "—"}</td>
										<td>{row.total_cpv_potential ?? "—"}</td>
									</tr>
								))}
						</tbody>
					</table>
				</div>
			</div>

			<div className="briefing-doc__section briefing-doc__strategy-header">
				<div className="briefing-doc__section-heading">Negotiation Strategy</div>
			</div>
			<div className="briefing-doc__section briefing-doc__strategy-body">
				{loadingTactics ? (
					<div className="briefing-doc__muted">Loading strategy…</div>
				) : (
					<>
						<div className="briefing-doc__strategy-tabs">
							<div className="briefing-doc__strategy-tab">Strategy</div>
							<div className="briefing-doc__strategy-tab">Counter Strategy</div>
							<div className="briefing-doc__strategy-tab">Concessions</div>
						</div>
						<div className="briefing-doc__strategy-grid">
							<div>
								{(tactics?.strategy || []).length === 0 && (
									<div className="briefing-doc__empty-state">No strategy data available</div>
								)}
								{(
									tactics?.strategy ||
									[]
								).map((item, idx) => {
									const { title, description, bullets } = normalizeTacticItem(item);
									const key = title || description || idx;
									const listItems = [
										...(description ? [description] : []),
										...bullets,
									];
									return (
										<div className="briefing-doc__strategy-card" key={key}>
											{title && <h4>{title}</h4>}
											{listItems.length > 0 && (
												<ul>
													{listItems.map((entry, entryIdx) => (
														<li key={`${key}-${entryIdx}`}>{entry}</li>
													))}
												</ul>
											)}
										</div>
									);
								})}
							</div>
							<div>
								{(tactics?.counter_strategy || []).length === 0 && (
									<div className="briefing-doc__empty-state">No negotiation approach available</div>
								)}
								{(
									tactics?.counter_strategy ||
									[]
								).map((item, idx) => {
									const { title, description, bullets } = normalizeTacticItem(item);
									const key = title || description || idx;
									const listItems = [
										...(description ? [description] : []),
										...bullets,
									];
									return (
										<div className="briefing-doc__strategy-card" key={key}>
											{title && <h4>{title}</h4>}
											{listItems.length > 0 && (
												<ul>
													{listItems.map((entry, entryIdx) => (
														<li key={`${key}-${entryIdx}`}>{entry}</li>
													))}
												</ul>
											)}
										</div>
									);
								})}
							</div>
							<div>
								{(tactics?.concessions || []).length === 0 && (
									<div className="briefing-doc__empty-state">No concession tactics available</div>
								)}
								{(
									tactics?.concessions ||
									[]
								).map((item, idx) => {
									const { title, description, bullets } = normalizeTacticItem(item);
									const key = title || description || idx;
									const listItems = [
										...(description ? [description] : []),
										...bullets,
									];
									return (
										<div className="briefing-doc__strategy-card" key={key}>
											{title && <h4>{title}</h4>}
											{listItems.length > 0 && (
												<ul>
													{listItems.map((entry, entryIdx) => (
														<li key={`${key}-${entryIdx}`}>{entry}</li>
													))}
												</ul>
											)}
										</div>
									);
								})}
							</div>
						</div>
					</>
				)}
			</div>
			<div className="briefing-doc__section briefing-doc__key-ask-header">
				<div className="briefing-doc__section-heading briefing-doc__key-ask-title">
					Risks &amp; Watch outs
				</div>
			</div>

			<div className="briefing-doc__risks-box">
				<div className="briefing-doc__risks-body">
					{loadingTactics ? (
						<div className="briefing-doc__muted">Loading risks…</div>
					) : (
						<>
							{resolvedRisks.length > 0 ? (
								<ul className="briefing-doc__risk-list">
									{resolvedRisks.map((item, idx) => {
										const key = `${item.title || "risk"}-${idx}`;
										return (
											<li className="briefing-doc__risk-card" key={key}>
												{item.title && (
													<div className="briefing-doc__risk-combined-title">
														{item.title}
													</div>
												)}
												{item.risk && (
													<div className="briefing-doc__risk-desc">
														<strong>Risk:</strong> {item.risk}
													</div>
												)}
												{item.watchout && (
													<div className="briefing-doc__risk-desc">
														<strong>Watchout:</strong> {item.watchout}
													</div>
												)}
											</li>
										);
									})}
								</ul>
							) : (
								<div className="briefing-doc__empty-state">No risks or watchouts available</div>
							)}
						</>
					)}
				</div>
			</div>

			<div className="briefing-doc__footer">
				<CustomButton
					label="Export Document"
					variant="primary"
					iconsOnly={false}
					icon="pi pi-download"
					disabled={
						exporting ||
						loadingSuppliers ||
						loadingTargets ||
						loadingKeyAsk ||
						loadingSummary ||
						loadingTactics ||
						loadingCurrentOffer
					}
					onClick={handleExport}
				/>
			</div>

			{/* Hidden export view for PDF capture */}
			<div
				ref={exportHiddenRef}
				className="briefing-doc briefing-doc__export-print"
				aria-hidden="true"
			>
				<h2>Briefing Document</h2>
				<p>
					<strong>Supplier:</strong> {displaySupplierName || "N/A"}
				</p>
				<p>
					<strong>Supplier Point of Contact:</strong> {displayPointOfContact}
				</p>

				<h3>Key Ask</h3>
				<ul>
					{(resolvedKeyAsk || []).map((item) => (
						<li key={`export-ask-${item}`}>{item}</li>
					))}
				</ul>

				<h3>Targets</h3>
				{(!loadingTargets && targets.length > 0) ? (
					targets.map((target, targetIdx) => {
						const summaryEntry = summaryByProduct.get(
							(target.product_description || "").trim().toLowerCase()
						);
						const exportSummaryCards = loadingSummary
							? []
							: buildSummaryCards(summaryEntry);
						return (
							<div key={`export-target-heading-${target.bid_id ?? targetIdx}`}>
								<h4>{target.product_description}</h4>
								<table className="briefing-doc__export-table">
									<thead>
										<tr>
											<th>Label</th>
											<th>Target Price</th>
											<th>Supplier Bid</th>
											<th>Notes</th>
										</tr>
									</thead>
									<tbody>
										{exportSummaryCards.length ? exportSummaryCards.map((card, cardIdx) => (
											<tr key={`export-target-${target.bid_id ?? targetIdx}-${card.label}-${cardIdx}`}>
												<td>
													<strong>{card.label}</strong>
													{card.subLabel ? (
														<div className="briefing-doc__export-sub">{card.subLabel}</div>
													) : null}
												</td>
												<td>{card.value || "-"}</td>
												<td>{card.supplierBid || "-"}</td>
												<td>
													{card.bullets?.length ? (
														<ul className="briefing-doc__export-list">
															{card.bullets.map((b, bIdx) => (
																<li key={`export-bullet-${target.bid_id ?? targetIdx}-${card.label}-${bIdx}`}>
																	{b || "-"}
																</li>
															))}
														</ul>
													) : (
														"-"
													)}
												</td>
											</tr>
										)) : (
											<tr>
												<td colSpan={4}>No summary available</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						);
					})
				) : (
					<table className="briefing-doc__export-table">
						<thead>
							<tr>
								<th>Label</th>
								<th>Target Price</th>
								<th>Supplier Bid</th>
								<th>Notes</th>
							</tr>
						</thead>
						<tbody>
							{fallbackSummaryCards.map((card) => (
								<tr key={`export-target-fallback-${card.label}`}>
									<td>
										<strong>{card.label}</strong>
										{card.subLabel ? (
											<div className="briefing-doc__export-sub">{card.subLabel}</div>
										) : null}
									</td>
									<td>{card.value || "-"}</td>
									<td>{card.supplierBid || "-"}</td>
									<td>
										{card.bullets?.length ? (
											<ul className="briefing-doc__export-list">
												{card.bullets.map((b) => (
													<li key={`export-bullet-fallback-${card.label}-${b}`}>{b}</li>
												))}
											</ul>
										) : (
											"-"
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				<h3>Current Offer</h3>
				<table className="briefing-doc__export-table">
					<thead>
						<tr>
							<th>Article</th>
							<th>Variant</th>
							<th>Pack</th>
							<th>Quality</th>
							<th>Country</th>
							<th>Incoterm</th>
							<th>EXW</th>
							<th>Logistics</th>
							<th>DDP</th>
							<th>Volume</th>
							<th>CPV</th>
						</tr>
					</thead>
					<tbody>
						{currentOfferRows.map((row, idx) => (
							<tr key={`export-offer-${row.article ?? idx}-${row.variant ?? idx}`}>
								<td>{row.article ?? "-"}</td>
								<td>{row.variant ?? "-"}</td>
								<td>{row.pack_size ?? "-"}</td>
								<td>{row.quality ?? "-"}</td>
								<td>{row.country ?? "-"}</td>
								<td>{row.incoterm ?? "-"}</td>
								<td>{row.unit_cost_exw ?? "-"}</td>
								<td>{row.unit_cost_logistics ?? "-"}</td>
								<td>{row.unit_cost_ddp ?? "-"}</td>
								<td>{row.proposed_volume_allocation ?? "-"}</td>
								<td>{row.total_cpv_potential ?? "-"}</td>
							</tr>
						))}
					</tbody>
				</table>

				<h3>Negotiation Strategy</h3>
				<h4>Strategy</h4>
				<ul>
					{(
						tactics?.strategy ||
						[]
					).map((item, idx) => {
						const { title, description, bullets } = normalizeTacticItem(item);
						const key = title || description || idx;
						return (
							<li key={`export-strat-${key}`}>
								{title && description ? (
									<>
										<strong>{title}: </strong>
										{description}
									</>
								) : title ? (
									<strong>{title}</strong>
								) : (
									description
								)}
								{bullets.length > 0 && (
									<ul className="briefing-doc__export-list">
										{bullets.map((b) => (
											<li key={`export-strat-b-${idx}-${b}`}>{b}</li>
										))}
									</ul>
								)}
							</li>
						);
					})}
				</ul>

				<h4>Counter Strategy</h4>
				<ul>
					{(
						tactics?.counter_strategy ||
						[]
					).map((item, idx) => {
						const { title, description, bullets } = normalizeTacticItem(item);
						const key = title || description || idx;
						return (
							<li key={`export-counter-${key}`}>
								{title && description ? (
									<>
										<strong>{title}: </strong>
										{description}
									</>
								) : title ? (
									<strong>{title}</strong>
								) : (
									description
								)}
								{bullets.length > 0 && (
									<ul className="briefing-doc__export-list">
										{bullets.map((b) => (
											<li key={`export-counter-b-${idx}-${b}`}>{b}</li>
										))}
									</ul>
								)}
							</li>
						);
					})}
				</ul>

				<h4>Concessions</h4>
				<ul>
					{(
						tactics?.concessions ||
						[]
					).map((item, idx) => {
						const { title, description, bullets } = normalizeTacticItem(item);
						const key = title || description || idx;
						return (
							<li key={`export-concession-${key}`}>
								{title && description ? (
									<>
										<strong>{title}: </strong>
										{description}
									</>
								) : title ? (
									<strong>{title}</strong>
								) : (
									description
								)}
								{bullets.length > 0 && (
									<ul className="briefing-doc__export-list">
										{bullets.map((b) => (
											<li key={`export-concession-b-${idx}-${b}`}>{b}</li>
										))}
									</ul>
								)}
							</li>
						);
					})}
				</ul>

				<h3>Risks &amp; Watchouts</h3>
				<ul>
					{resolvedRisks.length > 0 ? (
						resolvedRisks.map((item, idx) => (
							<li key={`export-risk-${item.title || "risk"}-${idx}`}>
								{item.title && (
									<div className="briefing-doc__export-risk-title">
										{item.title}
									</div>
								)}
								{item.risk && (
									<div>Risk: {item.risk}</div>
								)}
								{item.watchout && (
									<div>Watchout: {item.watchout}</div>
								)}
							</li>
						))
					) : (
						<li>No risks or watchouts available</li>
					)}
				</ul>
			</div>
		</div>
	);
};

type TargetAccordionProps = {
	title: string;
	targetCards: TargetCard[];
	defaultOpen?: boolean;
	onOpen?: () => void;
	goalsLoading?: boolean;
	dataFetched?: boolean;
};

const TargetAccordion = ({
	title,
	targetCards,
	defaultOpen = false,
	onOpen,
	goalsLoading = false,
	dataFetched = false,
}: TargetAccordionProps) => {
	const [open, setOpen] = useState(defaultOpen);
	const [goalsExpanded, setGoalsExpanded] = useState(false);

	useEffect(() => {
		if (open && onOpen) {
			onOpen();
		}
	}, [open, onOpen]);

	return (
		<div className={`briefing-doc__target-accordion ${open ? "is-open" : ""}`}>
			<button
				type="button"
				className="briefing-doc__target-toggle"
				onClick={() => setOpen((prev) => !prev)}
			>
				<span>{title}</span>
				<i className={`pi ${open ? "pi-chevron-up" : "pi-chevron-down"}`} />
			</button>
			{open && (
				<div className="briefing-doc__target-cards">
					{targetCards.length === 0 && (
						<div className="briefing-doc__muted">
							{goalsLoading
								? "Loading target summary…"
								: dataFetched
									? "No target data available."
									: "Expand to load target summary."}
						</div>
					)}
					{targetCards.map((target) => (
						<div className="" key={target.label}>
							<div className="briefing-doc__card-heading">{target.label}</div>
							<div className="briefing-doc__card-subheading">
								{target.subLabel}
							</div>
							<div
								className={`briefing-doc__card-value${(target as any).isBatna ? " briefing-doc__card-value-batna" : ""
									}`}
							>
								{target.value ?? ""}
							</div>
							{target.label === "Category Goals" && goalsLoading && (
								<div className="briefing-doc__muted">Generating category goals…</div>
							)}
							{!goalsLoading && target.bullets && target.bullets.length > 0 && (
								<ul
									className={`briefing-doc__card-list${target.label === "Category Goals" && !goalsExpanded
										? " briefing-doc__card-list--collapsed"
										: ""
										}`}
								>
									{target.bullets.map((bullet) => (
										<li key={bullet}>{bullet}</li>
									))}
								</ul>
							)}
							{target.label === "Category Goals" && target.bullets && target.bullets.length > 0 && (
								<button
									type="button"
									className="briefing-doc__toggle"
									onClick={() => setGoalsExpanded((prev) => !prev)}
								>
									{goalsExpanded ? "Collapse" : "Expand full goals"}
								</button>
							)}
							{target.supplierBid && (
								<div className="briefing-doc__card-supplier">
									<span className="briefing-doc__card-supplier-label">
										Supplier Bid
									</span>
									<span className="briefing-doc__card-supplier-value">{target.supplierBid}</span>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default BriefingDocument;
